import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  adminUsersBulkDepartmentApi,
  type BulkDepartmentPreviewResponse,
  type BulkDepartmentResponse,
  type BulkDepartmentRow,
} from '../api/client';
import { useAdminScope } from '../hooks/useAdminScope';
import {
  bulkDepartmentResultKey,
  bulkDepartmentTemplateCsv,
  isBulkDepartmentUnavailable,
  similarNewDepartments,
} from '../utils/bulkImport';

/**
 * CSV で部署を一括変更（課題 #94。2026-09-30 高尾さん了承。BE PR #152）。登録済みの利用者の部署をまとめて変える。
 *
 * 一括登録（招待）と同じ流れにする。
 * 1. CSV（email / department_name）を選び「内容を確認する」で、変えずに各行の結果を見る
 *    （該当する利用者の有無、今の部署と変更後の部署、新しく作られる部署と似ている部署の注意）
 * 2. 確かめてから「部署を変更する」で反映する。変えられる行だけを変える
 *
 * 年度替わりの異動や、部署が未設定の方への設定に使う（1 名ずつの変更では追いつかないため）。
 */
export default function AdminUsersBulkDepartment() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const adminScope = useAdminScope();
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** 確認したときの中身。反映では同じ中身を送る（確認後にファイルが変わっても取り違えない） */
  const [file, setFile] = useState<File | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<BulkDepartmentPreviewResponse | null>(null);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<BulkDepartmentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resultLabel = (r: string) => {
    const key = bulkDepartmentResultKey(r);
    return key ? t(`admin.usersBulkDepartment.result.${key}`) : r;
  };
  const errorMessage = (err: unknown) =>
    isBulkDepartmentUnavailable(err)
      ? t('admin.usersBulkDepartment.unavailable')
      : err instanceof Error
      ? err.message
      : t('common.error');

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    setPreview(null);
    setResult(null);
    setError(null);
    if (!picked) {
      setFile(null);
      return;
    }
    // 中身をこの時点で読み取っておく。確認のあとにファイルが書き換わっても、確認した中身を反映する
    const content = await picked.arrayBuffer();
    setFile(new File([content], picked.name, { type: picked.type || 'text/csv' }));
  };

  const handlePreview = async () => {
    const token = getToken();
    if (!token || !file || previewing) return;
    setPreviewing(true);
    setError(null);
    setPreview(null);
    setResult(null);
    try {
      setPreview(await adminUsersBulkDepartmentApi.preview(file, token));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const handleApply = async () => {
    const token = getToken();
    if (!token || !file || !preview || applying) return;
    const similar = similarNewDepartments(preview);
    const lines = [
      t('admin.usersBulkDepartment.confirm.change', { count: preview.change_rows }),
      preview.new_departments.length > 0
        ? t('admin.usersImport.confirm.newDepartments', { count: preview.new_departments.length })
        : null,
      similar.length > 0 ? t('admin.usersImport.confirm.similar', { count: similar.length }) : null,
      t('admin.usersBulkDepartment.confirm.ok'),
    ].filter(Boolean);
    if (!window.confirm(lines.join('\n'))) return;
    setApplying(true);
    setError(null);
    try {
      setResult(await adminUsersBulkDepartmentApi.apply(file, token));
      setPreview(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([bulkDepartmentTemplateCsv()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users-department.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const noDepartment = t('admin.usersBulkDepartment.noDepartment');
  const rowsTable = (rows: BulkDepartmentRow[], testId: string) => (
    <table className="admin-table admin-users-import-table" data-testid={testId}>
      <thead>
        <tr>
          <th scope="col">{t('admin.usersImport.column.row')}</th>
          <th scope="col">{t('auth.email')}</th>
          <th scope="col">{t('admin.usersBulkDepartment.column.current')}</th>
          <th scope="col">{t('admin.usersBulkDepartment.column.next')}</th>
          <th scope="col">{t('admin.usersImport.column.result')}</th>
          <th scope="col">{t('admin.usersImport.column.reasons')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.row} data-testid="bulk-department-row" className={`is-${r.result}`}>
            <td>{r.row}</td>
            <td>{r.email}</td>
            <td>{r.result === 'error' && r.current_department_name == null ? '' : r.current_department_name ?? noDepartment}</td>
            <td>{r.department_name ?? ''}</td>
            <td>
              <span className={`admin-users-import-badge is-${r.result}`}>{resultLabel(r.result)}</span>
            </td>
            <td>{(r.reasons ?? []).join('／')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="page admin-users-import-page">
      <h1>{t('admin.usersBulkDepartment.title')}</h1>
      <p className="admin-page-intro">{t('admin.usersBulkDepartment.intro')}</p>

      <section className="admin-card">
        <h2>{t('admin.usersImport.format.title')}</h2>
        <ul className="admin-users-import-format">
          <li>{t('admin.usersBulkDepartment.format.columns')}</li>
          <li>{t('admin.usersBulkDepartment.format.existingOnly')}</li>
          <li>{t('admin.usersBulkDepartment.format.limit')}</li>
          <li>{t('admin.usersBulkDepartment.format.newDepartment')}</li>
          <li>{t('admin.usersImport.format.save')}</li>
        </ul>
        {adminScope === 'restricted' && (
          <p className="admin-form-hint" data-testid="bulk-department-restricted-note">
            {t('admin.usersBulkDepartment.format.restricted')}
          </p>
        )}
        <button type="button" className="admin-btn-text" onClick={downloadTemplate}>
          {t('admin.usersImport.template')}
        </button>
      </section>

      <section className="admin-card admin-form">
        <div className="admin-form-group">
          <label htmlFor="users-bulk-department-file">{t('admin.usersImport.file')}</label>
          <input
            id="users-bulk-department-file"
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            disabled={previewing || applying}
          />
        </div>
        <button
          type="button"
          className="admin-btn-primary"
          onClick={handlePreview}
          disabled={!file || previewing || applying}
        >
          {previewing ? t('admin.usersImport.previewing') : t('admin.usersImport.preview')}
        </button>
        {error && (
          <p className="admin-form-error" role="alert">
            {error}
          </p>
        )}
      </section>

      {preview && (
        <section className="admin-card" data-testid="bulk-department-preview">
          <h2>{t('admin.usersBulkDepartment.previewTitle')}</h2>
          <p data-testid="bulk-department-preview-summary">
            {t('admin.usersBulkDepartment.previewSummary', {
              total: preview.total_rows,
              change: preview.change_rows,
              unchanged: preview.unchanged_rows,
              error: preview.error_rows,
            })}
          </p>

          {preview.new_departments.length > 0 && (
            <div className="admin-users-import-departments" data-testid="bulk-department-new-departments">
              <h3>{t('admin.usersImport.newDepartments')}</h3>
              <ul>
                {preview.new_departments.map((d) => (
                  <li key={d.name}>
                    {t('admin.usersImport.newDepartment', { name: d.name, count: d.row_count })}
                    {(d.similar_existing ?? []).map((s) => (
                      <p key={s.department_id} className="admin-users-import-similar" data-testid="bulk-department-similar">
                        {t('admin.usersImport.similar', { name: s.name, reason: s.reason })}
                      </p>
                    ))}
                    {(d.similar_new ?? []).map((s) => (
                      <p key={`new-${s.name}`} className="admin-users-import-similar" data-testid="bulk-department-similar">
                        {t('admin.usersImport.similarNew', { name: s.name, reason: s.reason })}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.warnings.length > 0 && (
            <ul className="admin-users-import-warnings">
              {preview.warnings.map((w, i) => (
                <li key={`${w.row}-${i}`}>{t('admin.usersImport.warning', { row: w.row, message: w.message })}</li>
              ))}
            </ul>
          )}

          {rowsTable(preview.rows, 'bulk-department-preview-rows')}

          {preview.change_rows > 0 ? (
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleApply}
              disabled={applying}
              data-testid="bulk-department-run"
            >
              {applying
                ? t('admin.usersBulkDepartment.applying')
                : t('admin.usersBulkDepartment.apply', { count: preview.change_rows })}
            </button>
          ) : (
            <p className="admin-form-hint">{t('admin.usersBulkDepartment.nothingToApply')}</p>
          )}
        </section>
      )}

      {result && (
        <section className="admin-card" data-testid="bulk-department-result">
          <h2>{t('admin.usersBulkDepartment.resultTitle')}</h2>
          <p data-testid="bulk-department-result-summary">
            {t('admin.usersBulkDepartment.resultSummary', {
              updated: result.updated,
              unchanged: result.unchanged,
              error: result.errors,
            })}
          </p>
          {result.created_departments.length > 0 && (
            <p>
              {t('admin.usersImport.createdDepartments', {
                names: result.created_departments.map((d) => d.name).join('、'),
              })}
            </p>
          )}
          {rowsTable(result.rows, 'bulk-department-result-rows')}
          <button type="button" className="admin-btn-text" onClick={reset}>
            {t('admin.usersImport.again')}
          </button>
        </section>
      )}

      <p className="admin-back">
        <Link to="/admin/users">{t('common.back')} → {t('admin.usersManage')}</Link>
      </p>
    </div>
  );
}
